import React, { useState } from "react";

import { getCookie } from "cookies-next";
import Moment from "moment";
import { FormattedMessage, defineMessages } from "react-intl";

import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";
import { Review } from "@/components/organisms/Review";
import { cn } from "@/lib/utils";
import { useClientEnvironment } from "src/app/environment/client";
import { getNextReviewPage } from "~/api/reviews";
import { useListingData } from "~/context/ListingPageDataContext";
import { useSegmentedListing } from "~/hooks/use-listing-page-data";
import { IReview } from "~/types/api-types";
import { ACCESS_TOKEN_COOKIE } from "~/utils/constants";

interface ReviewsProps {
  reviews: IReview[];
}

const vowelTestRegex = /^[aeiou].*/i;

const Reviews: React.FC<ReviewsProps> = ({ reviews }) => {
  const {
    data: { spaceReviewCount, listingParentSpaceId, listingId },
  } = useListingData();
  const { EXTERNAL_ADDRESS } = useClientEnvironment();
  // show the first 8 reviews and then we'll have a button to expose the rest
  const initialReviews = reviews.slice(0, 7);
  const [moreReviews, setMoreReviews] = useState(reviews.slice(7));
  const [reviewPage, setReviewPage] = useState(1);
  const [hasError, setHasError] = useState<React.ReactNode | string>("");

  const reviewTableIntl = defineMessages({
    booked: {
      id: "listing.review_table.booked",
      defaultMessage: "booked",
    },
    attended: {
      id: "listing.review_table.attended",
      defaultMessage: "attended",
    },
    an: {
      id: "listing.review_table.an",
      defaultMessage: "an",
    },
    a: {
      id: "listing.review_table.a",
      defaultMessage: "a",
    },
  });

  const bookAgainText = (
    <FormattedMessage
      id="listing.review_table.will_book_again"
      defaultMessage="I would book again."
    />
  );
  const notBookAgainText = (
    <FormattedMessage
      id="listing.review_table.will_not_book_again"
      defaultMessage="I would not book again."
    />
  );
  const noText = (
    <FormattedMessage id="listing.review_table.no" defaultMessage="No," />
  );
  const yesText = (
    <FormattedMessage id="listing.review_table.yes" defaultMessage="Yes," />
  );

  const buildOutReviews = ({
    reviewArray,
    showTopBorderOnLast,
    showTopBorderOnFirst = false,
  }: {
    reviewArray: ReviewsProps["reviews"];
    showTopBorderOnLast: boolean;
    showTopBorderOnFirst?: boolean;
  }) => {
    return (
      <div className="tw-w-full" id="reviews_section">
        {reviewArray.map((review, i) => {
          const isAttendeeReview = "attendee" === review.reviewer_type;
          const normalizedActivityType = isAttendeeReview
            ? review.normalized_activity_type
            : review.use_type;
          const aOrAn = vowelTestRegex.test(normalizedActivityType);
          const statementActivity = (
            <FormattedMessage
              id="listing.review_table.review_text"
              defaultMessage=" {reviewerTypeText} {aOrAn} {activityType} for {numberOfGuests} people"
              values={{
                reviewerTypeText: isAttendeeReview ? (
                  <FormattedMessage
                    {...reviewTableIntl.attended}
                    key="attended"
                  />
                ) : (
                  <FormattedMessage {...reviewTableIntl.booked} key="booked" />
                ),
                aOrAn: aOrAn ? (
                  <FormattedMessage {...reviewTableIntl.an} key="an" />
                ) : (
                  <FormattedMessage {...reviewTableIntl.a} key="a" />
                ),
                activityType: normalizedActivityType,
                numberOfGuests: review?.booking?.number_of_guests,
              }}
            />
          );
          const formattedDate = Moment.utc(review.date_created).format(
            "MMMM D, YYYY",
          );
          const reviewAvatarUrl = `https://img.peerspace.com/image/upload/c_crop,g_custom/f_auto,fl_progressive/c_scale,dpr_auto,h_100,w_100/${
            isAttendeeReview
              ? review.reviewer_avatar_id
              : review.guest?.avatar_id
          }`;

          const reviewerName = isAttendeeReview
            ? review.reviewer_name
            : review.guest?.name;

          const firstName = reviewerName?.first || "Unknown";
          const lastName = reviewerName?.last || "Unknown";

          return (
            <div
              key={`review-container-${i}`}
              className={cn("tw-py-24", {
                "tw-border-b-[1px] tw-border-gray-300":
                  i !== reviewArray.length - 1,
                "tw-border-t-[1px] tw-border-gray-300":
                  (i === reviewArray.length - 1 && showTopBorderOnLast) ||
                  (i === 0 && showTopBorderOnFirst),
              })}
            >
              <Review
                avatar={reviewAvatarUrl}
                firstName={firstName}
                lastName={lastName}
                reviewText={review.review_text}
                formattedDate={formattedDate}
                bookAgain={review.would_you_book_again}
                stars={review.stars}
                activityStatement={statementActivity}
                bookAgainText={bookAgainText}
                notBookAgainText={notBookAgainText}
                noText={noText}
                yesText={yesText}
                reviewerId={review.reviewer_id}
                profession={review.guest?.profession}
                isAttendeeReview={isAttendeeReview}
                productSchemaId={`${EXTERNAL_ADDRESS}/pages/listings/${listingId}`}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const reviewNumberDiff =
    spaceReviewCount - (reviews.length + moreReviews.length);
  const numberOfReviews = 8 < reviewNumberDiff ? 8 : reviewNumberDiff;
  const segmentedListing = useSegmentedListing();

  const getMoreReviews = async () => {
    try {
      setReviewPage(reviewPage + 1);
      const accessToken = getCookie(ACCESS_TOKEN_COOKIE) as string;
      const reviewLimit = reviewPage * 8;
      const moreReviews = await getNextReviewPage(
        listingParentSpaceId,
        8,
        reviewLimit,
        accessToken as string,
      );
      setMoreReviews((prevReviews) => [...prevReviews, ...moreReviews]);
      if (segmentedListing) {
        window.analyticsIntegrations.trackEvent(
          "see more reviews",
          segmentedListing as Record<string, unknown>,
        );
      }
    } catch {
      setHasError(
        <FormattedMessage
          id="hosting.on_submit_attendee_price_form.error_message"
          defaultMessage="Something went wrong, please try again."
        />,
      );
    }
  };

  return (
    // id="reviews" is used for scrolling from reviewHighlights.tsx - leave this
    <div className="tw-my-40" id="reviews">
      <Text typeface="headline-2-m" element="h5">
        <FormattedMessage
          id="listing.listing_main.grace_period_header_reviews"
          defaultMessage="Reviews ({space_review_count})"
          values={{ space_review_count: spaceReviewCount }}
        />
      </Text>
      {buildOutReviews({
        reviewArray: initialReviews,
        showTopBorderOnLast: false,
      })}
      <div className="tw-w-full">
        {/* extra reviews that are pulled and shown if user requests them */}
        {moreReviews.length > 0 && (
          <div>
            {buildOutReviews({
              reviewArray: moreReviews,
              showTopBorderOnLast: false,
              showTopBorderOnFirst: true,
            })}
          </div>
        )}
        {/* button to show more reviews */}
        {spaceReviewCount > 8 && numberOfReviews > 0 && (
          <Button
            appearance="white"
            variant="outline-2"
            onClick={() => getMoreReviews()}
          >
            <Text element="h2" typeface="body-2-m">
              <FormattedMessage
                id="listing.listing_main.more_reviews_button"
                defaultMessage="See {number_of_reviews} More {review_plural}"
                values={{
                  number_of_reviews: numberOfReviews,
                  review_plural: numberOfReviews > 1 ? "Reviews" : "Review",
                }}
              />
            </Text>
          </Button>
        )}
        {!!hasError && (
          <Text
            typeface="body-2"
            element="p"
            className="tw-text-red-800 tw-mt-8"
          >
            {hasError}
          </Text>
        )}
      </div>
    </div>
  );
};

export default Reviews;
